<template>
  <PageLayout :title="t('admin.navigation.insights.label')" class="!p-0">
    <div class="flex h-full grow">
      <div
        class="hidden sm:flex flex-col w-72 min-w-min border-r border-gray-200 bg-white pt-2 pb-2 overflow-y-scroll"
      >
        <div
          class="flex items-center space-x-2 pt-4 pl-8 text-xl font-medium text-gray-900"
        >
          <h1 class="font-semibold">
            {{ t("admin.settings.navigation.spaces.label") }}
          </h1>
        </div>
        <div class="mt-5 flex flex-grow flex-col overflow-scroll pb-8">
          <nav
            class="flex-1 space-y-8 bg-white px-4 sm:px-6 lg:px-8"
            aria-label="Sidebar"
          >
            <div class="space-y-1">
              <Link
                v-for="item in tenants"
                :key="item.name"
                :href="route('insights', [item.id])"
                :class="[
                  isActive(item.id)
                    ? 'bg-gray-50 text-gray-900 hover:text-gray-900 hover:bg-gray-100'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 cursor-pointer',
                  'group flex items-center px-2 py-2 text-sm font-medium rounded-sm',
                ]"
              >
                <span class="truncate flex-1">{{ item.name }}</span>
              </Link>
            </div>
          </nav>
        </div>
      </div>

      <div class="flex h-full grow">
        <div class="flex flex-grow flex-col">
          <div
            class="sticky top-0 z-20 space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
          >
            <div class="flex items-center">
              <h1 class="text-gray-900 text-xl font-medium">
                {{ tenant.name }}
              </h1>
            </div>
          </div>

          <div class="sm:hidden pt-6 px-6">
            <tenant-selector
              :tenants="tenants"
              :active="tenant.id"
              @tenant-selected="handleTenantSelected"
            />
          </div>

          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6 pb-28 sm:pb-0"
          >
            <div class="flex flex-col gap-6">
              <div
                class="flex justify-between items-start bg-white p-4 rounded-sm shadow-sm border"
              >
                <div class="flex flex-col">
                  <h2 class="text-lg font-medium text-gray-900">
                    {{ t("admin.insights.tasks.open.title") }}
                  </h2>
                  <p class="text-sm font-medium text-gray-600">
                    {{ t("admin.insights.tasks.open.description") }}
                  </p>
                </div>
                <div class="font-medium text-lg">
                  {{ insights.tasks.open.count }}
                </div>
              </div>
              <div
                class="flex flex-col bg-white p-4 rounded-md shadow-sm border"
              >
                <div class="flex justify-between items-start">
                  <div class="flex flex-col">
                    <h2 class="text-lg font-medium text-gray-900">
                      {{ t("admin.insights.tasks.scheduled.title") }}
                    </h2>
                    <p class="text-sm font-medium text-gray-600">
                      {{ t("admin.insights.tasks.scheduled.description") }}
                    </p>
                  </div>
                  <div class="font-medium text-lg">
                    {{ insights.tasks.scheduled.count }}
                  </div>
                </div>
                <div class="flex flex-col mt-4">
                  <div
                    class="flex justify-between mt-2"
                    v-for="assignee in insights.tasks.scheduled.assignees"
                    :key="assignee.id"
                  >
                    <div class="flex items-center space-x-2">
                      <user-avatar :user="assignee" />
                      <span class="text-sm font-medium text-gray-900">{{
                        assignee.full_name
                      }}</span>
                    </div>
                    <span class="text-sm font-medium text-gray-900">{{
                      assignee.task_count
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div
                class="flex flex-col bg-white p-4 rounded-md shadow-sm border"
              >
                <div class="flex justify-between items-start">
                  <div class="flex flex-col">
                    <h2 class="text-lg font-medium text-gray-900">
                      {{ t("admin.insights.tasks.completed.title") }}
                    </h2>
                    <p class="text-sm font-medium text-gray-600">
                      {{ t("admin.insights.tasks.completed.description") }}
                    </p>
                  </div>
                  <div class="font-medium text-lg">
                    {{ insights.tasks.completed.count }}
                  </div>
                </div>
                <div class="flex flex-col mt-4">
                  <div
                    class="flex justify-between mt-2"
                    v-for="assignee in insights.tasks.completed.assignees"
                    :key="assignee.id"
                  >
                    <div class="flex items-center space-x-2">
                      <user-avatar :user="assignee" />
                      <span class="text-sm font-medium text-gray-900">{{
                        assignee.full_name
                      }}</span>
                    </div>
                    <span class="text-sm font-medium text-gray-900">{{
                      assignee.task_count
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div
                class="flex flex-col bg-white p-4 rounded-md shadow-sm border"
              >
                <div class="flex justify-between items-start">
                  <div class="flex flex-col">
                    <h2 class="text-lg font-medium text-gray-900">
                      {{ t("admin.insights.categories.title") }}
                    </h2>
                    <p class="text-sm font-medium text-gray-600">
                      {{ t("admin.insights.categories.description") }}
                    </p>
                  </div>
                </div>
                <div class="flex flex-col mt-4">
                  <div
                    class="flex justify-between mt-2"
                    v-for="category in insights.categories"
                    :key="category.id"
                  >
                    <div class="flex items-center space-x-2">
                      <span class="text-sm font-medium text-gray-900">{{
                        category.name
                      }}</span>
                    </div>
                    <span class="text-sm font-medium text-gray-900">{{
                      category.task_count
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import PageLayout from "@/Layouts/PageLayout.vue";
import { Link, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import UserAvatar from "@/Tailwind/Components/UserAvatar.vue";
import TenantSelector from "@/Tailwind/Components/Tenants.vue";
import { router } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const tenant = usePage().props.tenant;
const tenants = usePage().props.tenants;
const insights = usePage().props.insights;

defineOptions({ layout: PersistentLayout });

function isActive(to) {
  return window.location.href.endsWith(to);
}

function handleTenantSelected(id) {
  router.get(route("insights", [id]));
}
</script>
